import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import ButtonText from 'components/shared/ButtonText';
import Text from 'components/shared/Text';
import Table from 'components/shared/Table';
import ModalCancelOrder from 'components/modals/CancelOrder';
import { formatCreatedAt } from 'helpers/dates';
import { formatAmount, formatAsset } from '@nbx/frontend-helpers/format';
import { orderIsNotCancellable, getFillsQuantitySum } from 'helpers/orders';

import styles from './styles.module.scss';

const microapps = window.MICROFRONTENDS_CONFIG;

const columnWidths = [1, 2, 2, 2, 3.5, 1.5];
const endAlignIndex = 5;

const generateKeyIndex = data => {
  return data.map(datum => datum.id);
};

export function OrdersOpen(props) {
  const { t } = useTranslation();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(null);

  const headers = [
    t('common_side'),
    t('common_amount'),
    t('common_price'),
    t('common_filled'),
    t('common_date'),
    ''
  ];

  const openCancelModal = order => {
    setCancellingOrder(order);
    setCancelModalOpen(true);
  };

  const closeCancelModal = order => {
    props.openOrders?.refetch();
    setCancelModalOpen(false);
    if (!order) {
      setCancellingOrder(null);
    }
  };

  useEffect(() => {
    if (
      cancellingOrder !== null &&
      !props.openOrders.refreshing &&
      !generateKeyIndex(props.openOrders.data).includes(cancellingOrder.id)
    ) {
      setCancellingOrder(null);
    }
  }, [cancellingOrder, props.openOrders.data, props.openOrders.refreshing, props.openOrders.keyIndex]);

  const renderItem = useCallback(
    (order, index) => {
      if (index === 5) {
        const isDisabled = orderIsNotCancellable(order.events);
        return (
          <ButtonText
            disabled={isDisabled}
            title={isDisabled ? t('market_order_cannot_be_cancelled') : undefined}
            onClick={() => openCancelModal(order)}
            styles={[styles.buttonCancel]}>
            {t('common_cancel')}
          </ButtonText>
        );
      }
      return {
        0: (
          <Text
            styles={[
              styles.cellText,
              { [styles.buy]: order.side === 'BUY', [styles.sell]: order.side === 'SELL' }
            ]}>
            {order.side === 'BUY' ? t('common_buy') : t('common_sell')}
          </Text>
        ),
        1: (
          <Text monospaced styles={[styles.cellText]}>
            {formatAsset(order.quantity, props.market.baseAsset, props.assets)}
          </Text>
        ),
        2: (
          <Text monospaced styles={[styles.cellText]}>
            {order.category === 'MARKET'
              ? t('common_market')
              : formatAmount(order.execution.price, props.market.minTickLength)}
          </Text>
        ),
        3: (
          <Text monospaced styles={[styles.cellText]}>
            {formatAsset(getFillsQuantitySum(order), props.market.baseAsset, props.assets)}
          </Text>
        ),
        4: (
          <Text monospaced styles={[styles.cellText, styles.createdAtText]}>
            {formatCreatedAt(order.events.createdAt, true)}
          </Text>
        )
      }[index];
    },
    [props.assets, props.market.baseAsset, props.market.minTickLength, t]
  );
  const renderLink = useCallback(order => `${microapps.routes.orders.path}/${order.id}`, []);

  const cancelling = cancellingOrder !== null && !cancelModalOpen;

  return (
    <>
      <Table
        columnWidths={columnWidths}
        data={props.openOrders.data}
        keyField="id"
        hasNextPage={props.openOrders.hasNextPage && !props.openOrders.pausedForEmptyPages}
        pausedForEmptyPages={props.openOrders.pausedForEmptyPages}
        isLoading={props.openOrders.isLoading || cancelling}
        fetchNextPage={props.openOrders.fetchNextPage}
        fetchAllPages={props.openOrders.fetchAllPages}
        isLoadingNextPage={props.openOrders.loadingNextPage}
        isLoadingAllPages={props.openOrders.loadingAllPages}
        setRefreshEndIndex={props.openOrders.setRefreshEndIndex}
        emptyTitle={t('market_no_orders_yet')}
        emptyMessage={t('market_once_you_place')}
        emptyShowBlob={false}
        endAlignIndex={endAlignIndex}
        headers={headers}
        renderItem={renderItem}
        renderLink={renderLink}
        borders={false}
        rowHeight={40}
        tableStyles={[styles.table]}
        headerStyles={[styles.headers]}
        hasMaxHeight
        hasActions // hasAction prop always assumes that the last column is clickable
      />
      {cancelModalOpen && (
        <ModalCancelOrder handleClose={closeCancelModal} marketId={props.market.id} order={cancellingOrder} />
      )}
    </>
  );
}

OrdersOpen.propTypes = {
  openOrders: PropTypes.object.isRequired,
  market: PropTypes.object.isRequired,
  assets: PropTypes.object.isRequired
};

export default OrdersOpen;
